import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { apiCall } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { User, DollarSign, GitFork, MessageSquare, Save, X } from "lucide-react";

export default function EditClientModal({ client, isOpen, onClose, onSaveSuccess, agents: propAgents }) {
  const [agents, setAgents] = useState(propAgents || []);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({});

  useEffect(() => {
    if (client) {
      setFormData({
        user_id: client.user_id || "",
        mobile_no: client.mobile_no && client.mobile_no !== "MASKED" ? client.mobile_no : "",
        username: client.username || client.client_name || "",
        site: client.site || "",
        branch: client.branch || "",
        registration_date: client.registration_date || "",
        total_deposit: client.total_deposit !== undefined && client.total_deposit !== null ? client.total_deposit : "",
        total_withdraw: client.total_withdraw !== undefined && client.total_withdraw !== null ? client.total_withdraw : "",
        deposit_count: client.deposit_count !== undefined && client.deposit_count !== null ? client.deposit_count : "",
        last_deposit: client.last_deposit || "",
        first_deposit_amount: client.first_deposit_amount !== undefined && client.first_deposit_amount !== null ? client.first_deposit_amount : "",
        first_deposit_date: client.first_deposit_date || "",
        category: client.category || "regular",
        assigned_to: client.assigned_to || "unassigned",
        source: client.source || "manual",
        last_call_response: client.last_call_response || "none",
        conversion_status: client.conversion_status || "none",
        last_call_notes: client.last_call_notes || client.notes || "",
        notes: client.notes || ""
      });
    }
  }, [client]);

  useEffect(() => {
    if (isOpen) {
      if (!propAgents || propAgents.length === 0) {
        apiCall("get", "/agents")
          .then(({ data }) => setAgents(data || []))
          .catch(() => {});
      }
    }
  }, [isOpen, propAgents]);

  if (!client) return null;

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        user_id: formData.user_id.trim(),
        mobile_no: formData.mobile_no.trim(),
        username: formData.username.trim(),
        site: formData.site.trim(),
        branch: (formData.branch || "").trim(),
        registration_date: formData.registration_date.trim(),
        total_deposit: formData.total_deposit === "" ? 0 : parseFloat(formData.total_deposit),
        total_withdraw: formData.total_withdraw === "" ? 0 : parseFloat(formData.total_withdraw),
        deposit_count: formData.deposit_count === "" ? 0 : parseInt(formData.deposit_count),
        last_deposit: formData.last_deposit.trim(),
        first_deposit_amount: formData.first_deposit_amount === "" ? 0 : parseFloat(formData.first_deposit_amount),
        first_deposit_date: formData.first_deposit_date.trim(),
        category: formData.category,
        assigned_to: formData.assigned_to === "unassigned" ? "" : formData.assigned_to,
        source: formData.source,
        last_call_response: formData.last_call_response === "none" ? null : formData.last_call_response,
        conversion_status: formData.conversion_status === "none" ? null : formData.conversion_status,
        last_call_notes: formData.last_call_notes,
        notes: formData.notes
      };

      const originalUserId = client.user_id;
      const { data } = await apiCall("put", `/clients/${originalUserId}`, payload);
      toast.success("Client details updated successfully!");
      if (onSaveSuccess) {
        onSaveSuccess(data.client || { ...client, ...payload });
      }
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update client details");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={open => !open && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto p-0 gap-0">
        <DialogHeader className="p-5 border-b border-border sticky top-0 bg-background z-10 flex flex-row items-center justify-between">
          <div>
            <DialogTitle className="text-lg font-bold flex items-center gap-2">
              Edit Client: {client.user_id}
              <Badge variant="outline" className="text-xs uppercase font-mono">{formData.category}</Badge>
            </DialogTitle>
            <p className="text-xs text-muted-foreground mt-0.5">Admin full access: Edit all details and columns</p>
          </div>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <Tabs defaultValue="basic" className="w-full">
            <TabsList className="grid grid-cols-4 w-full mb-4">
              <TabsTrigger value="basic" className="text-xs flex items-center gap-1">
                <User className="w-3.5 h-3.5" /> Basic
              </TabsTrigger>
              <TabsTrigger value="financials" className="text-xs flex items-center gap-1">
                <DollarSign className="w-3.5 h-3.5" /> Financials
              </TabsTrigger>
              <TabsTrigger value="assignment" className="text-xs flex items-center gap-1">
                <GitFork className="w-3.5 h-3.5" /> Assignment
              </TabsTrigger>
              <TabsTrigger value="status" className="text-xs flex items-center gap-1">
                <MessageSquare className="w-3.5 h-3.5" /> Status & Notes
              </TabsTrigger>
            </TabsList>

            {/* TAB 1: BASIC INFO */}
            <TabsContent value="basic" className="space-y-3 mt-0">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs font-semibold">User ID</Label>
                  <Input
                    value={formData.user_id}
                    onChange={e => handleChange("user_id", e.target.value)}
                    placeholder="e.g. 10245"
                    className="h-9 text-xs"
                    required
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs font-semibold">Mobile Number</Label>
                  <Input
                    value={formData.mobile_no}
                    onChange={e => handleChange("mobile_no", e.target.value)}
                    placeholder="e.g. 9876543210"
                    className="h-9 text-xs font-mono"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs font-semibold">Username / Name</Label>
                  <Input
                    value={formData.username}
                    onChange={e => handleChange("username", e.target.value)}
                    placeholder="e.g. rahul_sharma"
                    className="h-9 text-xs"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs font-semibold">Branch</Label>
                  <Input
                    value={formData.branch}
                    onChange={e => handleChange("branch", e.target.value)}
                    placeholder="e.g. Main Branch"
                    className="h-9 text-xs"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs font-semibold">Site Name</Label>
                  <Input
                    value={formData.site}
                    onChange={e => handleChange("site", e.target.value)}
                    placeholder="e.g. site1@gmail.com"
                    className="h-9 text-xs"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs font-semibold">Registration Date</Label>
                  <Input
                    type="text"
                    value={formData.registration_date}
                    onChange={e => handleChange("registration_date", e.target.value)}
                    placeholder="YYYY-MM-DD or DD/MM/YYYY"
                    className="h-9 text-xs"
                  />
                </div>
                <div className="space-y-1 sm:col-span-2">
                  <Label className="text-xs font-semibold">Source</Label>
                  <Select value={formData.source} onValueChange={v => handleChange("source", v)}>
                    <SelectTrigger className="h-9 text-xs"><SelectValue placeholder="Source" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="google_sheet">Google Sheet</SelectItem>
                      <SelectItem value="extension">Extension</SelectItem>
                      <SelectItem value="csv_upload">CSV Upload</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </TabsContent>

            {/* TAB 2: FINANCIALS */}
            <TabsContent value="financials" className="space-y-3 mt-0">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs font-semibold">Total Deposit (₹)</Label>
                  <Input
                    type="number"
                    step="any"
                    value={formData.total_deposit}
                    onChange={e => handleChange("total_deposit", e.target.value)}
                    placeholder="0.00"
                    className="h-9 text-xs font-mono text-green-600"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs font-semibold">Total Withdrawal (₹)</Label>
                  <Input
                    type="number"
                    step="any"
                    value={formData.total_withdraw}
                    onChange={e => handleChange("total_withdraw", e.target.value)}
                    placeholder="0.00"
                    className="h-9 text-xs font-mono text-red-500"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs font-semibold">Deposit Count</Label>
                  <Input
                    type="number"
                    value={formData.deposit_count}
                    onChange={e => handleChange("deposit_count", e.target.value)}
                    placeholder="0"
                    className="h-9 text-xs font-mono"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs font-semibold">Last Deposit Date / Time</Label>
                  <Input
                    type="text"
                    value={formData.last_deposit}
                    onChange={e => handleChange("last_deposit", e.target.value)}
                    placeholder="YYYY-MM-DD"
                    className="h-9 text-xs"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs font-semibold">First Deposit Amount (₹)</Label>
                  <Input
                    type="number"
                    step="any"
                    value={formData.first_deposit_amount}
                    onChange={e => handleChange("first_deposit_amount", e.target.value)}
                    placeholder="0.00"
                    className="h-9 text-xs font-mono"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs font-semibold">First Deposit Date</Label>
                  <Input
                    type="text"
                    value={formData.first_deposit_date}
                    onChange={e => handleChange("first_deposit_date", e.target.value)}
                    placeholder="YYYY-MM-DD"
                    className="h-9 text-xs"
                  />
                </div>
              </div>
            </TabsContent>

            {/* TAB 3: ASSIGNMENT & PIPELINE */}
            <TabsContent value="assignment" className="space-y-3 mt-0">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs font-semibold">Assigned Agent</Label>
                  <Select value={formData.assigned_to || "unassigned"} onValueChange={v => handleChange("assigned_to", v)}>
                    <SelectTrigger className="h-9 text-xs"><SelectValue placeholder="Select agent..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unassigned">-- Unassigned --</SelectItem>
                      {agents.map(a => (
                        <SelectItem key={a._id} value={a._id}>{a.name} ({a.email})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs font-semibold">Category</Label>
                  <Select value={formData.category} onValueChange={v => handleChange("category", v)}>
                    <SelectTrigger className="h-9 text-xs"><SelectValue placeholder="Category" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="fresh">Fresh</SelectItem>
                      <SelectItem value="hot">Hot</SelectItem>
                      <SelectItem value="regular">Regular</SelectItem>
                      <SelectItem value="cold">Cold</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </TabsContent>

            {/* TAB 4: STATUS & NOTES */}
            <TabsContent value="status" className="space-y-3 mt-0">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs font-semibold">Call Response / Status</Label>
                  <Select value={formData.last_call_response} onValueChange={v => handleChange("last_call_response", v)}>
                    <SelectTrigger className="h-9 text-xs"><SelectValue placeholder="Select status..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">-- None / Pending --</SelectItem>
                      <SelectItem value="interested">Interested</SelectItem>
                      <SelectItem value="not_interested">Not Interested</SelectItem>
                      <SelectItem value="follow_up">Follow Up</SelectItem>
                      <SelectItem value="not_connected">Not Connected</SelectItem>
                      <SelectItem value="converted">Converted</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs font-semibold">Conversion Status</Label>
                  <Select value={formData.conversion_status} onValueChange={v => handleChange("conversion_status", v)}>
                    <SelectTrigger className="h-9 text-xs"><SelectValue placeholder="Select conversion..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">-- None --</SelectItem>
                      <SelectItem value="pending">Pending Admin Approval</SelectItem>
                      <SelectItem value="approved">Approved</SelectItem>
                      <SelectItem value="rejected">Rejected</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-1">
                <Label className="text-xs font-semibold">Last Call Notes / Remarks</Label>
                <Textarea
                  value={formData.last_call_notes}
                  onChange={e => handleChange("last_call_notes", e.target.value)}
                  placeholder="Notes from agent or call remarks..."
                  rows={2}
                  className="text-xs"
                />
              </div>

              <div className="space-y-1">
                <Label className="text-xs font-semibold">Admin Internal Notes</Label>
                <Textarea
                  value={formData.notes}
                  onChange={e => handleChange("notes", e.target.value)}
                  placeholder="Special internal instructions or remarks for this client..."
                  rows={2}
                  className="text-xs"
                />
              </div>
            </TabsContent>
          </Tabs>

          <DialogFooter className="pt-3 border-t border-border flex items-center justify-end gap-2">
            <Button type="button" variant="outline" size="sm" onClick={onClose} disabled={loading}>
              <X className="w-3.5 h-3.5 mr-1" /> Cancel
            </Button>
            <Button type="submit" size="sm" disabled={loading}>
              {loading ? <div className="animate-spin h-3.5 w-3.5 border-2 border-primary-foreground border-t-transparent rounded-full mr-1" /> : <Save className="w-3.5 h-3.5 mr-1" />}
              Save Changes
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
